import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { saveInvoicingServices } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleInvoicingServiceDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'

interface VehiclesEligibleForInvoicingCardProps {
  invoicingServices: VehicleInvoicingServiceDto[]
  refreshInvoicing: (invoicing: InvoicingDto) => void
}

export default function VehiclesEligibleForInvoicingCard({ invoicingServices, refreshInvoicing }: VehiclesEligibleForInvoicingCardProps) {
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isSavingAndMarkingReadyToInvoice, setIsSavingAndMarkingReadyToInvoice] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleInvoicingServiceDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleInvoicingServiceDto[]>([])

  const [importCharge, setImportCharge] = useState<string>('')
  const [duty, setDuty] = useState<string>('')
  const [inboundTransportation, setInboundTransportation] = useState<string>('')
  const [clusterConversion, setClusterConversion] = useState<string>('')
  const [clusterProgramming, setClusterProgramming] = useState<string>('')
  const [clusterReplacement, setClusterReplacement] = useState<string>('')
  const [titleFee, setTitleFee] = useState<string>('')
  const [recallExpenses, setRecallExpenses] = useState<string>('')
  const [fedEx, setFedEx] = useState<string>('')
  const [fedExAPI, setFedExAPI] = useState<string>('')
  const [replacementLabel, setReplacementLabel] = useState<string>('')
  const [repairs, setRepairs] = useState<string>('')
  const [vehicleCleanUp, setVehicleCleanUp] = useState<string>('')
  const [miscellaneous, setMiscellaneous] = useState<string>('')
  const [outboundTransportation, setOutboundTransportation] = useState<string>('')

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  useEffect(() => {
    setIsSubmitting(isSaving || isSavingAndMarkingReadyToInvoice)
  }, [isSaving, isSavingAndMarkingReadyToInvoice])

  const onChangeSortByDirection = (propertyName: keyof VehicleInvoicingServiceDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  const each = (key: keyof VehicleInvoicingServiceDto, input: string): void => {
    const amount = parseFloat(input).toFixed(2)

    setSelected(selected.map(v => ({ ...v, [key]: amount })))
  }

  const split = (key: keyof VehicleInvoicingServiceDto, input: string): void => {
    console.log(input)
    const amount = (parseFloat(input) / selected.length).toFixed(2)

    setSelected(selected.map(v => ({ ...v, [key]: amount })))
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Invoicing Services
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableCell padding='checkbox' sx={{ width: '50px', minWidth: '50px' }}></TableCell>

                  <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingImportCharge'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Import Charge
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingDuty'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Duty
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingInboundTransportation'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='160px'
                  >
                    Inbound Transportation
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterConversion'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Conversion
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterProgramming'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Programming
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterReplacement'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Replacement
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingTitleFees'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Title Fee
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingRecallExpenses'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Recall Expenses
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingFedEx'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    FedEx
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingFedExAPI'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    FedEx API
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingReplacementLabel'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Replacement Label
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingRepairs'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Repairs
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingVehicleCleanUp'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Vehicle Clean Up
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingMiscellaneous'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Miscellaneous
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingOutboundTransportation'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='160px'
                  >
                    Outbound Transportation
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingNotes'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='200px'
                  >
                    Invoice Notes
                  </OrderableTableCell>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell padding='checkbox' sx={{ width: '50px', minWidth: '50px' }}></TableCell>

                    <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setImportCharge(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={importCharge ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setDuty(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={duty ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setInboundTransportation(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={inboundTransportation ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterConversion(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={clusterConversion ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterProgramming(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={clusterProgramming ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterReplacement(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={clusterReplacement ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setTitleFee(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={titleFee ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setRecallExpenses(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={recallExpenses ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setFedEx(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={fedEx ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setFedExAPI(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={fedExAPI ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setReplacementLabel(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={replacementLabel ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setRepairs(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={repairs ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setVehicleCleanUp(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={vehicleCleanUp ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setMiscellaneous(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={miscellaneous ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <TextField
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setOutboundTransportation(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        type='number'
                        value={outboundTransportation ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell padding='checkbox' sx={{ width: '50px', minWidth: '50px' }}></TableCell>

                    <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingImportCharge', importCharge)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingDuty', duty)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingInboundTransportation', inboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => split('invoicingInboundTransportation', inboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            SPLIT
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterConversion', clusterConversion)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterProgramming', clusterProgramming)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterReplacement', clusterReplacement)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingTitleFees', titleFee)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingRecallExpenses', recallExpenses)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingFedEx', fedEx)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingFedExAPI', fedExAPI)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingReplacementLabel', replacementLabel)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingRepairs', repairs)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingVehicleCleanUp', vehicleCleanUp)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingMiscellaneous', miscellaneous)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingOutboundTransportation', outboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => split('invoicingOutboundTransportation', outboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            SPLIT
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>
                  </TableRow>

                  {invoicingServices
                    // ?.sort(clientSideOrderBy<VehicleInvoicingServiceDto>(orderBy, order))
                    // .slice(...clientSidePagination(page, pageSize))
                    ?.map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox' sx={{ width: '50px', minWidth: '50px' }}>
                          <Checkbox
                            checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                            disabled={isSubmitting}
                            onChange={() => {
                              if (selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)) {
                                setSelected(selected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid))
                              } else {
                                setSelected([...selected, vehicle])
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ width: '200px', minWidth: '200px' }}>
                          <Typography>{vehicle.importerStockNumber}</Typography>
                          <Typography>{vehicle.buyerExporterCompany}</Typography>
                          <Typography>{vehicle.papsNumber}</Typography>
                          <Typography>{vehicle.vin}</Typography>
                          <Typography>
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingImportCharge: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingImportCharge
                                : vehicle.invoicingImportCharge
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingDuty: parseFloat(e.target.value) } : v)))
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingDuty
                                : vehicle.invoicingDuty
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v =>
                                  v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingInboundTransportation: parseFloat(e.target.value) } : v
                                )
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingInboundTransportation
                                : vehicle.invoicingInboundTransportation
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v =>
                                  v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingClusterConversion: parseFloat(e.target.value) } : v
                                )
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingClusterConversion
                                : vehicle.invoicingClusterConversion
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v =>
                                  v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingClusterProgramming: parseFloat(e.target.value) } : v
                                )
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingClusterProgramming
                                : vehicle.invoicingClusterProgramming
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v =>
                                  v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingClusterReplacement: parseFloat(e.target.value) } : v
                                )
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingClusterReplacement
                                : vehicle.invoicingClusterReplacement
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingTitleFees: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingTitleFees
                                : vehicle.invoicingTitleFees
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingRecallExpenses: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingRecallExpenses
                                : vehicle.invoicingRecallExpenses
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingFedEx: parseFloat(e.target.value) } : v)))
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingFedEx
                                : vehicle.invoicingFedEx
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingFedExAPI: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingFedExAPI
                                : vehicle.invoicingFedExAPI
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingReplacementLabel: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingReplacementLabel
                                : vehicle.invoicingReplacementLabel
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingRepairs: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingRepairs
                                : vehicle.invoicingRepairs
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingVehicleCleanUp: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingVehicleCleanUp
                                : vehicle.invoicingVehicleCleanUp
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingMiscellaneous: parseFloat(e.target.value) } : v))
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingMiscellaneous
                                : vehicle.invoicingMiscellaneous
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            onChange={e => {
                              setSelected(
                                selected.map(v =>
                                  v.vehicleGuid === vehicle.vehicleGuid ? { ...v, invoicingOutboundTransportation: parseFloat(e.target.value) } : v
                                )
                              )
                            }}
                            size='small'
                            sx={{ width: '100%' }}
                            type='number'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].invoicingOutboundTransportation
                                : vehicle.invoicingOutboundTransportation
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ width: '200px', minWidth: '200px' }}>{vehicle.invoicingNotes}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                {/* <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={invoicingServices.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter> */}
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Invoicing Services:**

This list displays vehicles that have been moved from the Vehicles Eligible For Invoicing list for charge adjustments.

**Edit Charges:**

Allows users to modify service charges by clicking into a cell and entering a value.

**Bulk Charge Updates:**

Enter a value in the top cell of a column to apply changes to all vehicles in the list.

Press "EACH" to apply the entered value to all vehicles in that column.

Press "SPLIT" to divide the entered value by the number of vehicles and apply the result to all vehicles.

**Save Button:**

Updates the invoicing fields under Vehicle Details → Invoicing (tab) with the values in the Invoicing Services list.

**Save And Mark Selected Vehicles As Ready To Invoice:**

Moves selected vehicles to the Ready For Invoicing list and populates the "Ready To Invoice" column with the current date.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSaving}
                onClick={() => {
                  setIsSaving(true)
                  saveInvoicingServices(selected, false)
                    .then(res => {
                      enqueueSnackbar('Invoicing Services Saved Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSaving(false))
                }}
                variant='contained'
              >
                SAVE
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndMarkingReadyToInvoice}
                onClick={() => {
                  setIsSavingAndMarkingReadyToInvoice(true)
                  saveInvoicingServices(selected, true)
                    .then(res => {
                      enqueueSnackbar('Invoicing Services Saved Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSavingAndMarkingReadyToInvoice(false))
                }}
                variant='contained'
              >
                SAVE AND MARK SELECTED VEHICLES AS READY TO INVOICE
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
